<div class="dashboard-container">
  <!-- Header -->
  <div class="dashboard-header">
    <div class="header-content">
      <h1>Tableau de Bord</h1>
      <p class="subtitle">{{ getCurrentDate() }}</p>
    </div>
    <div class="header-actions">
      <button pButton type="button" label="Actualiser" icon="pi pi-refresh" 
              class="p-button-outlined" (click)="refresh()"></button>
      <button pButton type="button" label="Nouvelle Comparaison" icon="pi pi-plus" 
              (click)="goToCompare()"></button>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <i class="pi pi-spin pi-spinner" style="font-size: 3rem;"></i>
    <p>Chargement des données...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !loading" class="error-state">
    <i class="pi pi-exclamation-triangle" style="font-size: 3rem; color: var(--red-500);"></i>
    <p>{{ error }}</p>
    <button pButton label="Réessayer" icon="pi pi-refresh" (click)="refresh()"></button>
  </div>

  <!-- Dashboard Content -->
  <div *ngIf="!loading && !error" class="dashboard-content">
    
    <!-- KPIs Section -->
    <h2 class="section-title">Métriques Clés</h2>
    
    <div class="kpis-grid">
      <div class="kpi-card">
        <div class="kpi-content">
          <span class="kpi-label">Projets ce Mois</span>
          <span class="kpi-value">{{ kpis?.projects_count || 0 }}</span>
          <span class="kpi-change" *ngIf="currentUserStats" 
                [class.positive]="currentUserStats.evolution_projects > 0"
                [class.negative]="currentUserStats.evolution_projects < 0">
            {{ currentUserStats.evolution_projects > 0 ? '+' : '' }}{{ currentUserStats.evolution_projects.toFixed(1) }}%
          </span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <span class="kpi-label">Taux de Conversion</span>
          <span class="kpi-value">{{ (kpis?.conversion_rate || 0).toFixed(1) }}%</span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <span class="kpi-label">Panier Moyen</span>
          <span class="kpi-value">{{ formatAmount(kpis?.average_basket || 0) }}</span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <span class="kpi-label">CA ce Mois</span>
          <span class="kpi-value">{{ formatAmount(currentUserStats?.amount_this_month || 0) }}</span>
          <span class="kpi-change" *ngIf="currentUserStats"
                [class.positive]="currentUserStats.evolution_amount > 0"
                [class.negative]="currentUserStats.evolution_amount < 0">
            {{ currentUserStats.evolution_amount > 0 ? '+' : '' }}{{ currentUserStats.evolution_amount.toFixed(1) }}%
          </span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <span class="kpi-label">Objectif Mensuel</span>
          <span class="kpi-value">{{ (kpis?.monthly_target_completion || 0).toFixed(0) }}%</span>
          <span class="kpi-subtitle">{{ kpis?.projects_count || 0 }} / 20 projets</span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <span class="kpi-label">Classement</span>
          <span class="kpi-value">#{{ currentUserStats?.rank || '-' }}</span>
          <span class="kpi-subtitle">sur {{ currentUserStats?.total_users || '-' }}</span>
        </div>
      </div>
    </div>

    <!-- Statistiques Détaillées -->
    <div class="stats-section">
      <h2 class="section-title">Performance</h2>

      <div class="stats-grid">
        <!-- Performance Mensuelle -->
        <div class="stat-card">
          <div class="stat-header">
            <h3>Performance Mensuelle</h3>
          </div>
          <div class="stat-body">
            <div class="stat-row">
              <span class="stat-label">Projets ce mois</span>
              <span class="stat-value-large">{{ currentUserStats?.projects_this_month || 0 }}</span>
              <span class="stat-evolution" [class.positive]="currentUserStats && currentUserStats.evolution_projects > 0" 
                    [class.negative]="currentUserStats && currentUserStats.evolution_projects < 0">
                {{ currentUserStats ? (currentUserStats.evolution_projects > 0 ? '+' : '') + currentUserStats.evolution_projects.toFixed(1) + '%' : '' }}
              </span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Projets le mois dernier</span>
              <span class="stat-value">{{ currentUserStats?.projects_last_month || 0 }}</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-row">
              <span class="stat-label">CA ce mois</span>
              <span class="stat-value-large">{{ formatAmount(currentUserStats?.amount_this_month || 0) }}</span>
              <span class="stat-evolution" [class.positive]="currentUserStats && currentUserStats.evolution_amount > 0" 
                    [class.negative]="currentUserStats && currentUserStats.evolution_amount < 0">
                {{ currentUserStats ? (currentUserStats.evolution_amount > 0 ? '+' : '') + currentUserStats.evolution_amount.toFixed(1) + '%' : '' }}
              </span>
            </div>
            <div class="stat-row">
              <span class="stat-label">CA le mois dernier</span>
              <span class="stat-value">{{ formatAmount(currentUserStats?.amount_last_month || 0) }}</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-row">
              <span class="stat-label">Projets traités</span>
              <span class="stat-value status-treated">{{ currentUserStats?.projects_treated || 0 }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Projets à traiter</span>
              <span class="stat-value status-pending">{{ currentUserStats?.projects_to_process || 0 }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Taux de traitement</span>
              <span class="stat-value-large">{{ getTreatmentRate().toFixed(1) }}%</span>
            </div>
          </div>
        </div>

        <!-- Objectifs -->
        <div class="stat-card">
          <div class="stat-header">
            <h3>Objectifs</h3>
          </div>
          <div class="stat-body">
            <div class="objective-progress">
              <div class="progress-header">
                <span>Objectif mensuel</span>
                <span class="progress-value">{{ kpis?.projects_count || 0 }} / 20</span>
              </div>
              <div class="progress-bar-container">
                <div class="progress-bar" [style.width.%]="(kpis?.monthly_target_completion || 0)"></div>
              </div>
              <span class="progress-text">{{ (kpis?.monthly_target_completion || 0).toFixed(0) }}% complété</span>
            </div>
            
            <div class="stat-divider"></div>
            
            <div class="stat-row">
              <span class="stat-label">Taux de conversion</span>
              <span class="stat-value-large">{{ (kpis?.conversion_rate || 0).toFixed(1) }}%</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Panier moyen</span>
              <span class="stat-value">{{ formatAmount(kpis?.average_basket || 0) }}</span>
            </div>
          </div>
        </div>

        <!-- Classement -->
        <div class="stat-card">
          <div class="stat-header">
            <h3>Classement</h3>
          </div>
          <div class="stat-body">
            <div class="ranking-info">
              <div class="rank-number">#{{ currentUserStats?.rank || '-' }}</div>
              <div class="rank-label">sur {{ currentUserStats?.total_users || '-' }} commerciaux</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistiques équipe (admin) -->
    <div class="stats-section" *ngIf="isAdmin">
      <h2 class="section-title">Statistiques Équipe (Admin)</h2>

      <div class="stat-card">
        <div class="stat-header">
          <h3>Traitement des projets par utilisateur</h3>
        </div>

        <div *ngIf="loadingAdminStats" class="admin-stats-loading">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Chargement des statistiques équipe...</span>
        </div>

        <p-table
          *ngIf="!loadingAdminStats"
          [value]="adminUsersStats"
          [paginator]="true"
          [rows]="8"
          [rowsPerPageOptions]="[8, 15, 30]"
          responsiveLayout="scroll"
          [tableStyle]="{ 'min-width': '52rem' }"
        >
          <ng-template pTemplate="header">
            <tr>
              <th>Utilisateur</th>
              <th>Email</th>
              <th>Total projets</th>
              <th>Traités</th>
              <th>À traiter</th>
              <th>Taux traitement</th>
              <th>Montant total</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-userStat>
            <tr>
              <td>{{ userStat.name }}</td>
              <td>{{ userStat.email }}</td>
              <td>{{ userStat.total_projects }}</td>
              <td><span class="status-treated">{{ userStat.projects_treated }}</span></td>
              <td><span class="status-pending">{{ userStat.projects_to_process }}</span></td>
              <td>{{ userStat.treatment_rate.toFixed(1) }}%</td>
              <td>{{ formatAmount(userStat.total_amount || 0) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="7">Aucune statistique disponible pour les autres utilisateurs.</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>

    <!-- Actions -->
    <div class="actions-section">
      <h2 class="section-title">Actions Rapides</h2>
      <div class="actions-grid">
        <button pButton type="button" label="Nouvelle Comparaison" 
                icon="pi pi-plus" (click)="goToCompare()"></button>
        <button pButton type="button" label="Historique des Projets" 
                icon="pi pi-list" class="p-button-outlined"
                (click)="goToHistory()"></button>
      </div>
    </div>

  </div>
</div>
